/**
 * NextActionPill — the single most important nudge on every page.
 *
 * Computes the user's next-best action from three signals and renders
 * a small pill in the page header. Self-hides when there's nothing
 * obvious to do (i.e., the user is fully set up).
 *
 * Decision tree:
 *   1. No readonly_role_arn  → "Next: Connect AWS"  → /idle-killer/connect
 *   2. No LLM proxy keys     → "Next: Mint proxy key" → /llm-proxy
 *   3. No cleanup candidates yet → "Next: Review savings opportunities" → /idle-killer
 *   4. Else: render nothing (user is set up, let them roam)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function NextActionPill() {
  const [next, setNext] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cc, keys, scan] = await Promise.all([
          axios.get(`${API}/idle-killer/cloud-connect`, auth()).catch(() => ({ data: {} })),
          axios.get(`${API}/llm-proxy/keys`, auth()).catch(() => ({ data: {} })),
          axios.post(`${API}/idle-killer/scan`, {}, auth()).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        if (!cc.data?.readonly_role_arn) {
          setNext({ label: 'Connect AWS account', href: '/idle-killer/connect' });
        } else if ((keys.data?.items || []).length === 0) {
          setNext({ label: 'Mint your proxy key', href: '/llm-proxy' });
        } else if ((scan.data?.candidate_count || 0) === 0) {
          setNext({ label: 'Review savings opportunities', href: '/idle-killer' });
        } else {
          setNext(null);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!next) return null;
  return (
    <Link
      to={next.href}
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20 text-xs font-medium transition-colors whitespace-nowrap"
      data-testid="next-action-pill"
    >
      Next: {next.label} <ArrowRight className="w-3 h-3" />
    </Link>
  );
}
